// Property API Service for Frontend
// This service communicates with Cloudflare Functions API endpoints

export interface PropertyInput {
  // Basic Property Info
  name: string;
  address: string;
  purchase_date: string;
  rental_start_date: string;
  // F15: date the rents and costs were entered; the engine grows them from here.
  prices_as_of?: string;
  purchase_price_eur: number;
  purchase_costs_eur?: number;
  annual_appreciation_pct?: number;
  
  // Property Details
  land_percentage_pct?: number;
  furniture_value_eur?: number;
  
  // Mortgage Details
  mortgage_rate_pct?: number;
  mortgage_term_years?: number;
  // F17: optional mortgage variants (absent = feature off)
  mortgage_interest_only_years?: number;
  mortgage_step_after_years?: number;
  mortgage_step_rate_pct?: number;
  mortgage_arrangement_fee_eur?: number;
  mortgage_fee_added_to_loan?: number | boolean;
  deposit_eur?: number;
  
  // Purchase Costs Breakdown
  notaire_fees_pct?: number;
  renovation_refurbishment_eur?: number;
  furnishing_equipment_eur?: number;
  legal_administrative_eur?: number;
  property_transfer_tax_pct?: number;
  purchase_agency_fees_eur?: number;   // acquisition agency fees, added to the CGT base
  capitalised_works_eur?: number;      // qualifying works that reduce the French capital gain
  
  // Sale Details
  sale_date?: string;
  sale_price_eur?: number;
  sale_notaire_fees_pct?: number;
  sale_legal_fees_eur?: number;
  sale_agency_fees_pct?: number;
  sale_other_fees_eur?: number;
  
  // Operating Costs
  management_fee_pct?: number;
  cleaning_per_booking_eur?: number;
  average_stay_length_days?: number;
  utilities_monthly_eur?: number;
  repairs_monthly_eur?: number;
  tourist_tax_per_guest_night_eur?: number;
  condo_fee_monthly_eur?: number;
  taxe_fonciere_annual_eur?: number;
  insurance_annual_eur?: number;
  accountant_france_annual_eur?: number;
  accountant_uk_annual_eur?: number;
  capex_provision_annual_pct?: number;
  
  // Location reference
  location_id: string;
  
  // Monte Carlo Parameters
  occupancy_uncertainty_low_season_std_dev_pct?: number;
  occupancy_uncertainty_high_season_std_dev_pct?: number;
  occupancy_uncertainty_event_std_dev_pct?: number;
  occupancy_uncertainty_film_festival_std_dev_pct?: number;
  
  // Economic Factors
  economic_impact_std_dev_pct?: number;
  economic_correlation?: number;
  
  // Sale Price Uncertainty
  property_value_growth_std_dev_pct?: number;
  
  // Weather Factors
  summer_weather_std_dev_pct?: number;
  weather_correlation?: number;
  
  // Monte Carlo Settings
  mc_antithetic_variables?: boolean;
  mc_occupancy_scenarios?: number;
  mc_scenario_jitter_std_dev_pct?: number;
  mc_random_seed?: number;
  
  // Currency Risk
  eur_gbp_volatility_pct?: number;
  currency_correlation?: number;
  
  // Market Risks
  competitor_supply_increase_std_dev_pct?: number;
  economic_recession_impact_std_dev_pct?: number;
  tourism_demand_shock_std_dev_pct?: number;
  
  // Property Risks
  major_repair_events_annual_prob?: number;
  property_damage_annual_prob?: number;
  regulatory_changes_prob?: number;
  
  // Operational Risks
  management_company_failure_prob?: number;
  legal_issues_annual_prob?: number;
  bad_guest_incidents_annual_prob?: number;
  
  // Tax Settings
  french_tax_regime?: string;
  fx_rate_income?: number;
  fx_rate_purchase_uk?: number;
  fx_rate_sale_uk?: number;
  years_owned?: number;
  uk_income_tax_rate_pct?: number;
  uk_cgt_annual_exempt_gbp?: number;
  total_building_depreciation_claimed_eur?: number;
}

export interface Property {
  id?: string;
  name: string;
  address: string;
  purchase_date: string;
  rental_start_date: string;
  // F15: date the rents and costs were entered; the engine grows them from here.
  prices_as_of?: string;
  purchase_price_eur: number;
  purchase_costs_eur: number;
  annual_appreciation_pct: number;
  land_percentage_pct: number;
  furniture_value_eur: number;
  mortgage_rate_pct: number;
  mortgage_term_years: number;
  deposit_eur: number;
  notaire_fees_pct: number;
  renovation_refurbishment_eur: number;
  furnishing_equipment_eur: number;
  legal_administrative_eur: number;
  property_transfer_tax_pct: number;
  sale_date?: string;
  sale_price_eur?: number;
  sale_notaire_fees_pct: number;
  sale_legal_fees_eur: number;
  sale_agency_fees_pct: number;
  sale_other_fees_eur: number;
  management_fee_pct: number;
  cleaning_per_booking_eur: number;
  average_stay_length_days: number;
  utilities_monthly_eur: number;
  repairs_monthly_eur: number;
  tourist_tax_per_guest_night_eur: number;
  condo_fee_monthly_eur: number;
  taxe_fonciere_annual_eur: number;
  insurance_annual_eur: number;
  accountant_france_annual_eur: number;
  accountant_uk_annual_eur: number;
  capex_provision_annual_pct: number;
  location_id: string;
  occupancy_uncertainty_low_season_std_dev_pct: number;
  occupancy_uncertainty_high_season_std_dev_pct: number;
  occupancy_uncertainty_event_std_dev_pct: number;
  occupancy_uncertainty_film_festival_std_dev_pct: number;
  economic_impact_std_dev_pct: number;
  economic_correlation: number;
  property_value_growth_std_dev_pct: number;
  summer_weather_std_dev_pct: number;
  weather_correlation: number;
  mc_antithetic_variables: boolean;
  mc_occupancy_scenarios: number;
  mc_scenario_jitter_std_dev_pct: number;
  mc_random_seed: number;
  eur_gbp_volatility_pct: number;
  currency_correlation: number;
  competitor_supply_increase_std_dev_pct: number;
  economic_recession_impact_std_dev_pct: number;
  tourism_demand_shock_std_dev_pct: number;
  major_repair_events_annual_prob: number;
  property_damage_annual_prob: number;
  regulatory_changes_prob: number;
  management_company_failure_prob: number;
  legal_issues_annual_prob: number;
  bad_guest_incidents_annual_prob: number;
  french_tax_regime: string;
  fx_rate_income: number;
  fx_rate_purchase_uk: number;
  fx_rate_sale_uk: number;
  years_owned: number;
  total_building_depreciation_claimed_eur: number;
  created_at: string;
  updated_at: string;
}

export interface PropertyWithSelection extends Property {
  isSelected: boolean;
}

export interface PropertyEvent {
  id: string;
  property_id: string;
  name: string;
  start_date: string;
  end_date: string;
  adr: number;
  occupancy_pct: number;
  probability: number;
  status: string;
  source?: string;
  created_at: string;
}

export interface PropertyOwnerStay {
  id: string;
  property_id: string;
  start_date: string;
  end_date: string;
  purpose: string;
  duration: number;
  created_at: string;
}

class PropertyAPIService {
  private baseURL: string;

  constructor() {
    // Use the current domain for API calls (Cloudflare Functions)
    this.baseURL = window.location.origin;
  }

  // Get all properties
  async getProperties(): Promise<Property[]> {
    try {
      const response = await fetch(`${this.baseURL}/api/properties`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw new Error('Failed to fetch properties');
    }
  }

  // Get a specific property by ID
  async getProperty(id: string): Promise<Property | null> {
    try {
      const response = await fetch(`${this.baseURL}/api/properties/${id}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching property:', error);
      throw new Error('Failed to fetch property');
    }
  }

  // Create a new property
  async createProperty(propertyData: PropertyInput): Promise<Property> {
    try {
      const response = await fetch(`${this.baseURL}/api/properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(propertyData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating property:', error);
      throw new Error('Failed to create property');
    }
  }

  // Update a property
  async updateProperty(id: string, propertyData: Partial<PropertyInput>): Promise<Property | null> {
    try {
      console.log('Sending update request with data:', propertyData);
      
      const response = await fetch(`${this.baseURL}/api/properties/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(propertyData),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error('Backend error response:', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Update successful, returned data:', result);
      return result;
    } catch (error) {
      console.error('Error updating property:', error);
      throw new Error(`Failed to update property: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Delete a property
  async deleteProperty(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/api/properties/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting property:', error);
      throw new Error('Failed to delete property');
    }
  }
}

// Export singleton instance
export const propertyAPI = new PropertyAPIService();
